import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';

/**
 * Seletor do ORÇAMENTO DE ORIGEM — usado ao derivar uma ordem de serviço ou um
 * contrato de um orçamento existente.
 *
 * Vivia duplicado dentro dos dois formulários (`ServiceOrderFormModal` e
 * `ContractFormModal`) com a mesma consulta e o mesmo cartão, divergindo só no
 * que cada um fazia com o resultado. As duas cópias já tinham a mesma regra de
 * seleção — `.not('status','in','("cancelled","rejected")')` — e manter isso
 * sincronizado em dois arquivos é o começo de toda divergência silenciosa.
 *
 * O que muda por chamador é o CONSUMO, não a busca: por isso `onPick` entrega o
 * orçamento com itens e fotos, e quem chama monta o que precisa (a OS materializa
 * os itens; o contrato compõe a descrição do serviço a partir dos nomes).
 */
export function QuotePickerDialog({
  open,
  onOpenChange,
  companyId,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null;
  onPick: (quote: any, items: any[], photos: any[]) => void;
}) {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('quotes')
        .select('*')
        .eq('company_id', companyId)
        .not('status', 'in', '("cancelled","rejected")')
        .order('created_at', { ascending: false })
        .limit(50);
      if (!cancelled) {
        setQuotes((data ?? []) as any[]);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  const pick = async (quote: any) => {
    const [itemsRes, photosRes] = await Promise.all([
      supabase.from('quote_items').select('*').eq('quote_id', quote.id).order('position', { ascending: true }),
      supabase.from('quote_photos').select('*').eq('quote_id', quote.id).order('position', { ascending: true }),
    ]);
    onPick(quote, (itemsRes.data ?? []) as any[], (photosRes.data ?? []) as any[]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Selecione um orçamento</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Carregando…</p>
        ) : quotes.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Nenhum orçamento encontrado</p>
        ) : (
          <div className="space-y-2">
            {quotes.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => pick(q)}
                className="w-full rounded-md border p-3 text-left transition hover:bg-muted"
              >
                <p className="font-medium">{q.title || q.client_name || 'Orçamento'}</p>
                <p className="text-xs text-muted-foreground">
                  {q.client_name ? `${q.client_name} · ` : ''}
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                    Number(q.total ?? 0),
                  )}
                </p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
